// Note: Twilio operations should typically be done server-side for security
// This is a client-side implementation for demo purposes
// In production, use Supabase Edge Functions or a backend service

import { supabase } from './supabase.js';

class TwilioService {
  constructor() {
    this.accountSid = import.meta.env.VITE_TWILIO_ACCOUNT_SID;
    this.authToken = import.meta.env.VITE_TWILIO_AUTH_TOKEN;
    this.fromNumber = import.meta.env.VITE_TWILIO_PHONE_NUMBER;
  }

  async sendSMS(to, message, userId, messageType) {
    try {
      // Log the SMS attempt
      const { data: logEntry, error: logError } = await supabase
        .from('sms_logs')
        .insert([{
          user_id: userId,
          message_type: messageType,
          message: message,
          status: 'queued'
        }])
        .select()
        .single();

      if (logError) throw logError;

      // In a real implementation, this would call a backend API
      // For now, we'll simulate the SMS sending
      console.log(`SMS to ${to}: ${message}`);

      // Update log status
      await supabase
        .from('sms_logs')
        .update({ status: 'sent' })
        .eq('id', logEntry.id);

      return { success: true, logId: logEntry.id };
    } catch (error) {
      console.error('Error sending SMS:', error);
      return { success: false, error: error.message };
    }
  }

  formatPhoneNumber(phoneNumber) {
    // Remove all non-numeric characters
    const cleaned = phoneNumber.replace(/\D/g, '');
    
    // Add +1 for US numbers if not present
    if (cleaned.length === 10) {
      return `+1${cleaned}`;
    } else if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return `+${cleaned}`;
    }
    
    return phoneNumber;
  }

  validatePhoneNumber(phoneNumber) {
    const e164Regex = /^\+[1-9]\d{1,14}$/;
    return e164Regex.test(phoneNumber);
  }

  truncateMessage(message, maxLength = 160) {
    if (message.length <= maxLength) return message;
    return message.substring(0, maxLength - 3) + '...';
  }

  // Message templates
  templates = {
    dailyPlanningReminder: (name) => 
      `Time to plan your Big 3 for today! 📝 Reply STOP to unsubscribe.`,
    
    dailyCheckinReminder: (name) => 
      `How did your Big 3 go today? Mark them complete! ✅ Reply STOP to unsubscribe.`,
    
    familyTimeInvite: (inviterName, activity, time) => 
      `${inviterName} wants ${activity} ${time}. Reply Y/N or check app. Reply STOP to unsubscribe.`,
    
    familyActivityVoting: (activityTitle) => 
      `New Family Event to vote on: ${activityTitle}! Check app to vote. Reply STOP to unsubscribe.`,
    
    confirmedFamilyPlan: (activity, datetime) => 
      `Confirmed: ${activity} ${datetime}! 🎬 Reply STOP to unsubscribe.`,
    
    newIdea: (ideaTitle, category) => 
      `New ${category} added to your ideas: ${ideaTitle}! 📚 Reply STOP to unsubscribe.`,
    
    dailyDigest: (updates) => {
      const parts = [];
      if (updates.ideas > 0) parts.push(`${updates.ideas} new ideas`);
      if (updates.invites > 0) parts.push(`${updates.invites} family invite${updates.invites > 1 ? 's' : ''}`);
      if (updates.activities > 0) parts.push(`${updates.activities} activity vote${updates.activities > 1 ? 's' : ''}`);
      if (updates.confirmed > 0) parts.push(`${updates.confirmed} confirmed plan${updates.confirmed > 1 ? 's' : ''}`);
      
      return `Today's updates: ${parts.join(', ')}. Check app for details! 📱 Reply STOP to unsubscribe.`;
    },
    
    testMessage: () => 
      `Test message from Paper Sedge! Your SMS notifications are working. Reply STOP to unsubscribe.`
  };
}

export const twilioService = new TwilioService();